import { useState, useEffect } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Megaphone, Plus, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', branch_id: '' });

  const isAdmin = ['super_admin', 'management'].includes(user?.system_role);

  const fetchData = async () => {
    try {
      const [annRes, brRes] = await Promise.all([
        api.get('/announcements'),
        api.get('/branches')
      ]);
      setAnnouncements(annRes.data);
      setBranches(brRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    try {
      await api.post('/announcements', {
        ...form,
        branch_id: form.branch_id === 'all' ? null : form.branch_id
      });
      toast.success('Announcement published — push notifications dispatched to subscribers');
      setDialogOpen(false);
      setForm({ title: '', content: '', branch_id: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const getBranchName = (bid) => {
    if (!bid) return 'All Branches';
    return branches.find(b => b.id === bid)?.name || 'Unknown';
  };

  const formatDate = (isoStr) => {
    try {
      return new Date(isoStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return isoStr; }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="announcements-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Announcements</h1>
          <p className="text-neutral-500 text-sm mt-1">Corporate and branch-level updates</p>
        </div>
        {isAdmin && (
          <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="create-announcement-button"
            onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> New Announcement
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-28 bg-neutral-200 rounded animate-pulse" />)}</div>
      ) : announcements.length === 0 ? (
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="py-12 text-center text-neutral-500">
            <Megaphone className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No announcements yet
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map(ann => (
            <Card key={ann.id} className="border border-neutral-200 shadow-sm rounded-md hover:shadow-md transition-shadow duration-200"
              data-testid={`announcement-card-${ann.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-4 min-w-0">
                    <div className="w-10 h-10 bg-red-50 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Megaphone className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-neutral-900">{ann.title}</h3>
                      <p className="text-sm text-neutral-600 mt-1.5 whitespace-pre-wrap">{ann.content}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-neutral-400">
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(ann.created_at)}</span>
                        <span>By: {ann.created_by_name}</span>
                        <span className="text-red-600 font-medium">{getBranchName(ann.branch_id)}</span>
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => handleDelete(ann.id)}
                      data-testid={`delete-announcement-${ann.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-neutral-400 hover:text-red-500" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Announcement Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="create-announcement-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">New Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Title</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="rounded-sm" data-testid="announcement-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Content</Label>
              <Textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                rows={4} className="rounded-sm" data-testid="announcement-content-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Visibility</Label>
              <Select value={form.branch_id || 'all'} onValueChange={v => setForm(p => ({ ...p, branch_id: v === 'all' ? '' : v }))}>
                <SelectTrigger className="rounded-sm" data-testid="announcement-branch-select">
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches (Corporate)</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreate} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="announcement-submit-button">
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
